'use client';

import { Suspense, useEffect, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { useAuth } from '@/lib/contexts/auth-context';
import { authApi } from '@/lib/api/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { Mail } from 'lucide-react';
import { useBotProtection } from '@/lib/hooks/use-bot-protection';
import AuthLayout from '@/components/auth/auth-layout';
import { storePendingClaimToken } from '@/lib/shared-link-claim';
import { apiErrorMessage } from '@/lib/api/errors';

function LoginContent() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [showUnverifiedAlert, setShowUnverifiedAlert] = useState(false);
  const { login } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();
  // Derived at render time. `claim` carries the token itself; `claimPending` is
  // a bare marker used when register bounces someone here because their address
  // already had an account — the real token is already in sessionStorage by
  // then, and a placeholder in `claim` would overwrite it on arrival.
  const hasClaim =
    searchParams.get('claim') !== null || searchParams.get('claimPending') !== null;

  // An anonymous short link can arrive here as well as at /auth/register: the
  // website offers signing in for people whose e-mail already has an account,
  // who previously had no way to claim a link at all. The authenticated layout
  // redeems the token once the sign-in succeeds.
  useEffect(() => {
    const claimToken = searchParams.get('claim');
    if (claimToken) {
      storePendingClaimToken(claimToken);
    }
  }, [searchParams]);
  const { isReady: botReady, getBotProtection, refresh: refreshBotProtection } = useBotProtection();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setShowUnverifiedAlert(false);

    try {
      await login(email, password);
      toast.success('Welcome back!');
      const returnTo = sessionStorage.getItem('post_login_return');
      sessionStorage.removeItem('post_login_return');
      router.push(returnTo?.startsWith('/') && !returnTo.startsWith('//') ? returnTo : '/');
    } catch (error) {
      const status = (error as { response?: { status?: number } }).response?.status;
      // Always a string, even for a 422's validation array (FRONTEND-WEB-USER-14).
      const errorMessage = apiErrorMessage(error, 'Invalid credentials');

      // Check if error is due to unverified email (403 status)
      if (status === 403 && errorMessage.includes('not verified')) {
        setShowUnverifiedAlert(true);
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    setResending(true);

    try {
      const response = await authApi.resendVerification({ email, bot_protection: getBotProtection() });
      toast.success(response.message);
      setShowUnverifiedAlert(false);
      refreshBotProtection();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to resend verification email'));
    } finally {
      setResending(false);
    }
  };

  return (
    <AuthLayout>
      {/* Header */}
      <div className="text-center space-y-2">
        <h1 className="text-3xl font-bold tracking-tight">Welcome back</h1>
        <p className="text-muted-foreground">
          {hasClaim
            ? "Sign in and we'll add your short link to your workspace"
            : 'Sign in to manage your links'}
        </p>
      </div>

      {/* Unverified email alert */}
      {showUnverifiedAlert && (
        <Alert className="border-border bg-muted/50">
          <Mail className="h-4 w-4 text-muted-foreground" />
          <AlertDescription className="ml-2">
            <p className="font-semibold text-foreground mb-1">
              Email not verified
            </p>
            <p className="text-sm text-muted-foreground mb-3">
              We&apos;ve sent a verification email to your inbox. Didn&apos;t receive it?
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleResendVerification}
              disabled={resending || !botReady}
            >
              {resending ? 'Sending...' : 'Send another email'}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Login form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
              className="h-11"
            />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password">Password</Label>
              <Link href="/auth/forgot-password" className="text-sm text-primary hover:underline" tabIndex={-1}>
                Forgot password?
              </Link>
            </div>
            <Input
              id="password"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
              className="h-11"
            />
          </div>
        </div>

        <Button type="submit" className="w-full h-11" disabled={loading}>
          {loading ? 'Signing in...' : 'Sign In'}
        </Button>

        <p className="text-sm text-center text-muted-foreground">
          Don&apos;t have an account?{' '}
          <Link href="/auth/register" className="text-primary hover:underline font-medium">
            Sign up
          </Link>
        </p>
      </form>
    </AuthLayout>
  );
}

// useSearchParams (added for ?claim=) suspends during prerender, so the page
// needs a boundary — same shape RegisterPage already uses for the same reason.
export default function LoginPage() {
  return (
    <Suspense>
      <LoginContent />
    </Suspense>
  );
}
